// TodoListPage.js
// Main page showing all todos for the family

import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from '../firebase';

// Import components
import TodoItem from '../components/TodoItem';
import BottomNav from '../components/BottomNav';
import TaskInspectModal from '../components/TaskInspectModal';

// Import admin context
import { useAdmin } from '../contexts/AdminContext';

function TodoListPage({ user, userProfile }) {
  // Admin mode - use viewingFamilyId if set, otherwise user's own familyId
  const { isInAdminView, viewingFamilyId, viewingFamilyData } = useAdmin();
  const effectiveFamilyId = isInAdminView ? viewingFamilyId : userProfile?.familyId;
  // Store all todos from Firestore
  const [todos, setTodos] = useState([]);

  // Store family data (for member names)
  const [familyData, setFamilyData] = useState(null);

  // Filters
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterDueDateStart, setFilterDueDateStart] = useState('');
  const [filterDueDateEnd, setFilterDueDateEnd] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'done'

  // Track selected task for inspect modal
  const [selectedTask, setSelectedTask] = useState(null);

  // Loading state
  const [loading, setLoading] = useState(true);

  // Premium status
  const [isPremium, setIsPremium] = useState(false);

  // Check if family is premium
  const checkPremiumStatus = async (familyLicenseCode) => {
    if (!familyLicenseCode) return false;

    try {
      const purchasesQuery = query(
        collection(db, 'purchases'),
        where('licenseCode', '==', familyLicenseCode),
        where('status', '==', 'active')
      );

      const snapshot = await getDocs(purchasesQuery);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking premium status:', error);
      return false;
    }
  };

  // Fetch family data when component loads (or when admin switches family)
  useEffect(() => {
    let isMounted = true;

    const fetchFamilyData = async () => {
      // If in admin view, use viewingFamilyData directly
      if (isInAdminView && viewingFamilyData) {
        setFamilyData(viewingFamilyData);
        const premium = await checkPremiumStatus(viewingFamilyData.licenseCode);
        if (isMounted) {
          setIsPremium(premium);
        }
        return;
      }

      if (effectiveFamilyId) {
        const familyDoc = await getDoc(doc(db, 'families', effectiveFamilyId));
        if (isMounted && familyDoc.exists()) {
          const data = familyDoc.data();
          setFamilyData(data);

          // Check premium status
          const premium = await checkPremiumStatus(data.licenseCode);
          if (isMounted) {
            setIsPremium(premium);
          }
        }
      }
    };

    fetchFamilyData();

    return () => {
      isMounted = false;
    };
  }, [effectiveFamilyId, isInAdminView, viewingFamilyData]);

  // Set up real-time listener for todos
  useEffect(() => {
    // Make sure we have a family ID
    if (!effectiveFamilyId) {
      setLoading(false);
      return;
    }

    // Create a query for todos belonging to this family
    // Ordered by createdAt so newest appear first
    const todosQuery = query(
      collection(db, 'todos'),
      where('familyId', '==', effectiveFamilyId),
      orderBy('createdAt', 'desc')
    );

    // onSnapshot sets up a real-time listener
    // It calls our callback whenever data changes
    const unsubscribe = onSnapshot(todosQuery, (snapshot) => {
      const todosData = [];

      snapshot.forEach((doc) => {
        todosData.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setTodos(todosData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching todos:', error);
      setLoading(false);
    });

    // Cleanup: stop listening when component unmounts
    return () => unsubscribe();
  }, [effectiveFamilyId]);

  // Handle autosaving task from inspect modal (doesn't close modal)
  const handleAutoSaveTask = async (updatedTask) => {
    try {
      const wasAI = selectedTask?.assignee === 'ai';
      const isNowAI = updatedTask.assignee === 'ai';

      // Update the todo document
      await updateDoc(doc(db, 'todos', updatedTask.id), {
        title: updatedTask.title,
        status: updatedTask.status,
        assignee: updatedTask.assignee || null,
        assigneeName: updatedTask.assigneeName || '',
        dueDate: updatedTask.dueDate || null,
        notes: updatedTask.notes || '',
        comments: updatedTask.comments || [],
        updatedAt: serverTimestamp()
      });

      // If assignee changed to AI and wasn't AI before, create an ai_tasks document
      // Note: in admin mode, we still use the viewed family's ID
      if (isNowAI && !wasAI && isPremium) {
        await addDoc(collection(db, 'ai_tasks'), {
          todoId: updatedTask.id,
          familyId: effectiveFamilyId,
          title: updatedTask.title,
          details: updatedTask.notes,
          requestedBy: user.uid,
          requestedByEmail: user.email,
          status: 'pending',
          createdAt: serverTimestamp()
        });
      }

      // Update selectedTask to reflect saved state (for AI check on next save)
      setSelectedTask(prev => prev ? { ...prev, assignee: updatedTask.assignee } : null);
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  // Get current user's display name for comments
  const currentUserDisplayName = familyData?.memberNames?.[user.uid] || 'You';

  // Filter todos based on filters
  const getFilteredTodos = () => {
    let filtered = todos;

    // Status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(todo => todo.status !== 'done');
    } else if (filterStatus === 'done') {
      filtered = filtered.filter(todo => todo.status === 'done');
    }
    // 'all' means no status filtering

    // Assignee filter
    if (filterAssignee) {
      if (filterAssignee === 'unassigned') {
        filtered = filtered.filter(todo => !todo.assignee || todo.assignee === '');
      } else {
        filtered = filtered.filter(todo => todo.assignee === filterAssignee);
      }
    }

    // Due date range filter
    if (filterDueDateStart) {
      const startDate = new Date(filterDueDateStart);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(todo => {
        if (!todo.dueDate) return false;
        const todoDate = new Date(todo.dueDate);
        todoDate.setHours(0, 0, 0, 0);
        return todoDate >= startDate;
      });
    }

    if (filterDueDateEnd) {
      const endDate = new Date(filterDueDateEnd);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(todo => {
        if (!todo.dueDate) return false;
        const todoDate = new Date(todo.dueDate);
        todoDate.setHours(0, 0, 0, 0);
        return todoDate <= endDate;
      });
    }

    return filtered;
  };

  const filteredTodos = getFilteredTodos();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading todos...</p>
      </div>
    );
  }

  return (
    <div className="todo-list-page">
      {/* Header with family name */}
      <div className="todo-header">
        {familyData?.name && (
          <div className="todo-header-family-name">{familyData.name}</div>
        )}
        <h1>To Do List</h1>
      </div>

      {/* Compact filters */}
      <div className="compact-filters">
        <div className="compact-filter-wrapper">
          <label className="compact-filter-label">Assignee</label>
          <select
            value={filterAssignee}
            onChange={(e) => setFilterAssignee(e.target.value)}
            className={`compact-filter ${filterAssignee ? 'active' : ''}`}
          >
            <option value="">All</option>
            {familyData?.memberNames && Object.entries(familyData.memberNames).map(([userId, name]) => (
              <option key={userId} value={userId}>
                {name}
              </option>
            ))}
            <option value="ai">🤖 AI</option>
            <option value="unassigned">Unassigned</option>
          </select>
        </div>
        <div className="compact-filter-wrapper">
          <label className="compact-filter-label">From</label>
          <input
            type="date"
            value={filterDueDateStart}
            onChange={(e) => setFilterDueDateStart(e.target.value)}
            className={`compact-filter ${filterDueDateStart ? 'active' : ''}`}
          />
        </div>
        <div className="compact-filter-wrapper">
          <label className="compact-filter-label">To</label>
          <input
            type="date"
            value={filterDueDateEnd}
            onChange={(e) => setFilterDueDateEnd(e.target.value)}
            className={`compact-filter ${filterDueDateEnd ? 'active' : ''}`}
          />
        </div>
        <div className="compact-filter-wrapper">
          <label className="compact-filter-label">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`compact-filter ${filterStatus !== 'all' ? 'active' : ''}`}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="done">Done</option>
          </select>
        </div>
      </div>

      {/* Todo list */}
      <div className="todo-list">
        {filteredTodos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              {filterStatus === 'done' ? '✓' : '📝'}
            </div>
            <p>
              {filterStatus === 'done'
                ? 'No completed todos yet'
                : filterAssignee === user.uid
                  ? 'No todos assigned to you'
                  : filterAssignee === 'ai'
                    ? 'No todos assigned to AI'
                    : filterAssignee === 'unassigned'
                      ? 'No unassigned todos'
                      : 'No todos yet. Tap + to add one!'}
            </p>
          </div>
        ) : (
          filteredTodos.map((todo) => (
            <TodoItem
              key={todo.id}
              todo={todo}
              currentUserId={user.uid}
              familyData={familyData}
              userEmail={user.email}
              isPremium={isPremium}
              onInspect={() => setSelectedTask(todo)}
            />
          ))
        )}
      </div>


      {/* Task inspect modal */}
      {selectedTask && (
        <TaskInspectModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onAutoSave={handleAutoSaveTask}
          familyData={familyData}
          currentUserId={user.uid}
          currentUserDisplayName={currentUserDisplayName}
          isPremium={isPremium}
          isAdminMode={isInAdminView}
        />
      )}

      <BottomNav user={user} userProfile={userProfile} />
    </div>
  );
}

export default TodoListPage;
